if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const completePackItems = this.getCompletePackItems();
        const variantPriceCents = this.getSelectedVariantPriceCents();
        const skipMainVariant = Number.isFinite(variantPriceCents) && variantPriceCents <= 0;

        if (skipMainVariant && !completePackItems.length) {
          this.setErrorMsgState('Este producto no se puede anadir al carrito.');
          return;
        }

        const quickAddDrawer = this.closest('quick-add-drawer');
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        let data = null;
        if (!skipMainVariant) {
          const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
          data = await response.json();
          let error = typeof data.description === 'string' ? data.description : data.message;
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', '));
          }

          if (data.status) this.setErrorMsgState(error);

          if (!response.ok) throw new Error(response.status);
        }
        if (completePackItems.length) {
          await this.addCompletePackItems(completePackItems);
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          if (data) {
            ProductForm.updateCartIcon(data);
          } else {
            this.refreshCartIconBubble();
          }

          // If item was added from Quick Add drawer, show "Added to cart" message.
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              if (completePackItems.length || !data) {
                this.cartDrawer.refresh(true);
                if (!quickAddDrawer && theme.settings.afterAtc === 'drawer') {
                  this.cartDrawer.open();
                }
              } else {
                this.cartDrawer.renderContents(
                  data,
                  !quickAddDrawer && theme.settings.afterAtc === 'drawer'
                );
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            } else if (completePackItems.length || !data) {
              this.refreshCartIconBubble();
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        if (!data) {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: completePackItems[0]?.id || null
            }
          }));
          return;
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    getSelectedVariantPriceCents() {
      const productSection = this.closest('.js-product');
      if (!productSection) return null;

      const variantIdInput = this.form?.querySelector('[name="id"]');
      const selectedVariantId = Number(variantIdInput?.value) || 0;

      const variantPicker = productSection.querySelector('variant-picker');
      if (variantPicker?.variant && Number(variantPicker.variant.id) === selectedVariantId) {
        const pickerPrice = Number(variantPicker.variant.price);
        if (Number.isFinite(pickerPrice)) return pickerPrice;
      }

      const variantDataEl = variantPicker?.querySelector('[type="application/json"]');
      if (variantDataEl) {
        try {
          const variantData = JSON.parse(variantDataEl.textContent);
          if (Number(variantData?.id) === selectedVariantId) {
            const variantPrice = Number(variantData?.price);
            if (Number.isFinite(variantPrice)) return variantPrice;
          }
        } catch (error) {
          console.log(error); // eslint-disable-line
        }
      }

      const completePackRoot = productSection.querySelector('[data-completa-pack-root]');
      if (completePackRoot) {
        const fallbackPrice = Number(completePackRoot.dataset.basePrice);
        if (Number.isFinite(fallbackPrice)) return fallbackPrice;
      }

      return null;
    }

    getCompletePackItems() {
      const productSection = this.closest('.js-product');
      if (!productSection) return [];

      const items = [];
      productSection.querySelectorAll('[data-completa-pack-root]').forEach((completePack) => {
        completePack.querySelectorAll('[data-completa-pack-item]').forEach((row) => {
          const qtyInput = row.querySelector('.qty-input__input');
          if (!qtyInput) return;

          const quantity = Number(qtyInput.value) || 0;
          const id = Number(row.dataset.variantId) || 0;
          if (!id || quantity <= 0) return;

          items.push({ id, quantity });
        });
      });

      return items;
    }

    async addCompletePackItems(items) {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();
      if (!response.ok) {
        const error = typeof data.description === 'string' ? data.description : data.message;
        if (error) this.setErrorMsgState(error);
        throw new Error(response.status);
      }

      return data;
    }

    async refreshCartIconBubble() {
      try {
        const response = await fetch('?sections=cart-icon-bubble');
        if (!response.ok) return;
        const sections = await response.json();
        ProductForm.updateCartIcon({ sections });
      } catch (error) {
        console.log(error); // eslint-disable-line
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
